if (!customElements.get('swiper-component')) {
  customElements.define(
    'swiper-component', 
    class SwiperComponent extends HTMLElement {
      constructor(params) {
        super();
      }

      connectedCallback() {
        this.initialize();
      }

      disconnectedCallback() {
        this.slider.destroy();
      }

      initialize() {
        this.sliderEl = this.querySelector('.swiper');
        this.options = JSON.parse(this.querySelector('[data-swiper-options]').textContent);
        
        this.slider = new Swiper(this.sliderEl, this.options);
      }
    }
  );
}

if (!customElements.get('instagram-stories')) {
  customElements.define(
    'instagram-stories', 
    class InstagramStories extends HTMLElement {
      constructor() {
        super();
        this.currentSlideIndex = 0;
        this.currentReelIndex = 0;
        this.totalSlides = 0;
        this.videoElements = [];
        this.isPlaying = false;
        this.isModalOpen = false;
        this.initialized = false;
        this.isMuted = false;
        
        // Bind methods
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleVideoEnded = this.handleVideoEnded.bind(this);
      }

      connectedCallback() {
        this.initialize();
      }

      disconnectedCallback() {
        this.cleanup();
      }

      initialize() {
        if (this.initialized) return;
        
        this.sliderEl = this.querySelector('.insta-stories__slider');
        this.modal = this.querySelector('.insta-stories__modal');
        this.categories = this.querySelectorAll('.insta-stories__category');
        
        // Initialize Swiper
        this.slider = new Swiper(this.sliderEl, {
          loop: false,
          slidesPerView: 1,
          allowTouchMove: false,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          on: {
            slideChange: (swiper) => {
              this.handleSlideChange(swiper.activeIndex);
            }
          }
        });

        this.totalSlides = this.slider.slides.length;
        this.initializeVideoDurations();
        this.initialized = true;
      }

      initializeVideoDurations() {
        // Clear existing video elements
        this.videoElements = [];
        
        // Get all slides
        const slides = this.querySelectorAll('.swiper-slide');
        
        slides.forEach((slide, slideIndex) => {
          const reels = slide.querySelectorAll('.insta-stories__reel');
          const progressLevels = slide.querySelectorAll('.insta-stories__progress-level');
          
          reels.forEach((reel, reelIndex) => {
            const video = reel.querySelector('video');
            if (video) {
              // Store video element with metadata
              const videoObj = {
                slideIndex,
                reelIndex,
                video,
                duration: 0,
                progressLevel: progressLevels[reelIndex],
                handleEnded: null
              };
              
              this.videoElements.push(videoObj);

              // Get video duration when metadata is loaded
              const setVideoDuration = () => {
                videoObj.duration = video.duration;
                
                // Set video duration as CSS custom property with 's' unit
                if (videoObj.progressLevel) {
                  videoObj.progressLevel.style.setProperty(
                    '--video-duration', 
                    `${video.duration}s`
                  );
                }
              };

              // Remove existing event listeners
              video.removeEventListener('loadedmetadata', setVideoDuration);
              
              // Add new event listener
              video.addEventListener('loadedmetadata', setVideoDuration);
              
              // Also try to load metadata if already available
              if (video.readyState >= 1) {
                setVideoDuration();
              }
              
              // Create video ended handler
              videoObj.handleEnded = () => {
                this.handleVideoEnded({ detail: { slideIndex, reelIndex } });
              };
              
              // Remove existing ended event listener and add new one
              video.removeEventListener('ended', videoObj.handleEnded);
              video.addEventListener('ended', videoObj.handleEnded);
              
              // Set initial muted state for all videos
              video.muted = this.isMuted;
            }
          });
        });
      }

      openModal(categoryIndex) {
        if (categoryIndex >= 0 && categoryIndex < this.totalSlides) {
          // Reset all slides first
          this.resetAllSlides();
          
          // Reset states
          this.currentSlideIndex = categoryIndex;
          this.currentReelIndex = 0;
          this.isPlaying = false;
          
          // Go to the selected slide
          this.slider.slideTo(categoryIndex, 0);
          
          // Show modal
          this.modal.classList.add('is-opened');
          this.isModalOpen = true;
          
          // Bind slide-specific controls
          this.bindSlideControls();
          
          // Update progress indicators for current slide
          this.updateProgressIndicators();
          
          // Start playing the first reel
          this.startAutoplay();
          
          // Add keyboard listener
          document.addEventListener('keydown', this.handleKeydown);
          
          // Prevent body scrolling
          document.body.classList.add('modal-open');
        }
      }

      closeModal() {
        this.modal.classList.remove('is-opened');
        this.isModalOpen = false;
        this.isPlaying = false;
        
        // Stop all videos
        this.stopAutoplay();
        
        // Reset all slides
        this.resetAllSlides();
        
        // Unbind slide controls
        this.unbindSlideControls();
        
        // Reset current indices
        this.currentSlideIndex = 0;
        this.currentReelIndex = 0;
        
        // Remove keyboard listener
        document.removeEventListener('keydown', this.handleKeydown);
        
        // Allow body scrolling
        document.body.classList.remove('modal-open');
      }

      resetAllSlides() {
        // Reset all slides - pause videos, reset progress, hide reels
        const slides = this.querySelectorAll('.swiper-slide');
        
        slides.forEach((slide, slideIndex) => {
          // Get all videos in this slide
          const videos = slide.querySelectorAll('video');
          videos.forEach(video => {
            video.pause();
            video.currentTime = 0;
          });
          
          // Hide all reels in this slide
          const reels = slide.querySelectorAll('.insta-stories__reel');
          reels.forEach(reel => {
            reel.classList.add('hide');
          });
          
          // Reset progress indicators for this slide
          const progressLevels = slide.querySelectorAll('.insta-stories__progress-level');
          progressLevels.forEach(level => {
            level.classList.remove('active', 'completed');
          });
        });
        
        // Also reset video elements state
        this.videoElements.forEach(videoObj => {
          if (videoObj.video) {
            videoObj.video.pause();
            videoObj.video.currentTime = 0;
            videoObj.video.muted = this.isMuted;
          }
        });
      }

      resetOtherSlides(currentSlideIndex) {
        // Reset all slides except the current one
        const slides = this.querySelectorAll('.swiper-slide');
        
        slides.forEach((slide, slideIndex) => {
          if (slideIndex !== currentSlideIndex) {
            // Get all videos in this slide
            const videos = slide.querySelectorAll('video');
            videos.forEach(video => {
              video.pause();
              video.currentTime = 0;
            });
            
            // Hide all reels in this slide
            const reels = slide.querySelectorAll('.insta-stories__reel');
            if (reels.length > 0) {
              // Hide all reels first
              reels.forEach(reel => {
                reel.classList.add('hide');
              });
              // Then show the first reel
              reels[0].classList.remove('hide');
            }
            
            // Reset progress indicators for this slide
            const progressLevels = slide.querySelectorAll('.insta-stories__progress-level');
            progressLevels.forEach(level => {
              level.classList.remove('active', 'completed');
            });
          } else {
            // For current slide, show the first reel
            const reels = slide.querySelectorAll('.insta-stories__reel');
            if (reels.length > 0) {
              // Hide all reels first
              reels.forEach(reel => {
                reel.classList.add('hide');
              });
              // Then show the first reel
              reels[0].classList.remove('hide');
            }
          }
        });
      }

      bindSlideControls() {
        const currentSlide = this.slider.slides[this.currentSlideIndex];
        
        // Find controls in current slide
        this.currentCloseBtn = currentSlide.querySelector('.insta-stories__close');
        this.currentVolumeBtn = currentSlide.querySelector('.insta-stories__volume');
        this.currentPrevBtn = currentSlide.querySelector('.insta-stories__navigation-prev');
        this.currentNextBtn = currentSlide.querySelector('.insta-stories__navigation-next');
        this.currentProgressLevels = currentSlide.querySelectorAll('.insta-stories__progress-level');
        
        // Bind event listeners
        if (this.currentCloseBtn) {
          this.currentCloseBtn.onclick = () => this.closeModal();
        }
        
        if (this.currentVolumeBtn) {
          this.currentVolumeBtn.onclick = () => this.toggleVolume();
          // Update volume button state based on global mute state
          this.updateVolumeButtonState();
        }
        
        if (this.currentPrevBtn) {
          this.currentPrevBtn.onclick = () => this.goToPreviousReel();
        }
        
        if (this.currentNextBtn) {
          this.currentNextBtn.onclick = () => this.goToNextReel();
        }
      }

      updateVolumeButtonState() {
        if (this.currentVolumeBtn) {
          if (this.isMuted) {
            this.currentVolumeBtn.classList.add('muted');
          } else {
            this.currentVolumeBtn.classList.remove('muted');
          }
        }
      }

      unbindSlideControls() {
        // Remove event listeners
        if (this.currentCloseBtn) {
          this.currentCloseBtn.onclick = null;
        }
        
        if (this.currentVolumeBtn) {
          this.currentVolumeBtn.onclick = null;
        }
        
        if (this.currentPrevBtn) {
          this.currentPrevBtn.onclick = null;
        }
        
        if (this.currentNextBtn) {
          this.currentNextBtn.onclick = null;
        }
        
        // Clear references
        this.currentCloseBtn = null;
        this.currentVolumeBtn = null;
        this.currentPrevBtn = null;
        this.currentNextBtn = null;
        this.currentProgressLevels = null;
      }

      startAutoplay() {
        if (!this.isModalOpen) return;
        
        this.stopAutoplay();
        this.isPlaying = true;
        
        // Get current video
        const currentVideo = this.getCurrentVideo();
        if (currentVideo && currentVideo.video) {
          // Hide all reels in current slide
          this.hideAllReels();
          
          // Show current reel
          const currentSlide = this.slider.slides[this.currentSlideIndex];
          const reels = currentSlide.querySelectorAll('.insta-stories__reel');
          if (reels[this.currentReelIndex]) {
            reels[this.currentReelIndex].classList.remove('hide');
          }
          
          // Reset video to beginning
          currentVideo.video.currentTime = 0;
          
          // Apply mute state
          currentVideo.video.muted = this.isMuted;
          
          // Play video
          const playPromise = currentVideo.video.play();
          
          if (playPromise !== undefined) {
            playPromise.then(() => {
              // Video started playing successfully
            }).catch(error => {
              console.log("Autoplay prevented:", error);
              // If autoplay is prevented, wait for user interaction
              this.waitForUserInteraction();
            });
          }
        }
      }

      stopAutoplay() {
        this.isPlaying = false;
        
        // Pause current video
        const currentVideo = this.getCurrentVideo();
        if (currentVideo && currentVideo.video) {
          currentVideo.video.pause();
        }
      }

      getCurrentVideo() {
        return this.videoElements.find(v => 
          v.slideIndex === this.currentSlideIndex && v.reelIndex === this.currentReelIndex
        );
      }

      getCurrentProgressLevel() {
        if (!this.currentProgressLevels) return null;
        return this.currentProgressLevels[this.currentReelIndex];
      }

      hideAllReels() {
        const currentSlide = this.slider.slides[this.currentSlideIndex];
        const reels = currentSlide.querySelectorAll('.insta-stories__reel');
        reels.forEach(reel => reel.classList.add('hide'));
      }

      handleVideoEnded(event) {
        if (!this.isModalOpen || !this.isPlaying) return;
        
        const { slideIndex, reelIndex } = event.detail;
        
        // Only handle if it's the current video
        if (slideIndex !== this.currentSlideIndex || reelIndex !== this.currentReelIndex) {
          return;
        }
        
        // Mark current progress as completed
        const currentProgress = this.getCurrentProgressLevel();
        if (currentProgress) {
          currentProgress.classList.remove('active');
          currentProgress.classList.add('completed');
        }
        
        // Move to next reel
        const currentSlide = this.slider.slides[this.currentSlideIndex];
        const totalReelsInSlide = currentSlide.querySelectorAll('.insta-stories__reel').length;
        
        if (this.currentReelIndex < totalReelsInSlide - 1) {
          // Move to next reel in same slide
          this.currentReelIndex++;
          this.updateProgressIndicators();
          this.startAutoplay();
        } else {
          // Move to next slide
          this.goToNextSlide();
        }
      }

      handleSlideChange(newIndex) {
        // Stop any playing video
        this.stopAutoplay();
        
        // Reset all other slides except the new one
        this.resetOtherSlides(newIndex);
        
        // Unbind previous slide controls
        this.unbindSlideControls();
        
        // Reset to first reel in new slide
        this.currentSlideIndex = newIndex;
        this.currentReelIndex = 0;
        
        // Reset all videos in new slide to beginning
        this.videoElements.forEach(videoObj => {
          if (videoObj.slideIndex === this.currentSlideIndex && videoObj.video) {
            videoObj.video.currentTime = 0;
            // Apply mute state to all videos in the new slide
            videoObj.video.muted = this.isMuted;
          }
        });
        
        // Bind new slide controls
        this.bindSlideControls();
        
        // Update progress indicators to show first reel as active
        this.updateProgressIndicators();
        
        // Start playing if modal is open
        if (this.isModalOpen) {
          this.startAutoplay();
        }
      }

      updateProgressIndicators() {
        if (!this.currentProgressLevels) return;
        
        // Reset all progress levels
        this.currentProgressLevels.forEach(level => {
          if (level) {
            level.classList.remove('active', 'completed');
          }
        });
        
        // Mark previous reels as completed
        for (let i = 0; i < this.currentReelIndex; i++) {
          if (this.currentProgressLevels[i]) {
            this.currentProgressLevels[i].classList.add('completed');
          }
        }
        
        // Mark current reel as active
        if (this.currentProgressLevels[this.currentReelIndex]) {
          this.currentProgressLevels[this.currentReelIndex].classList.add('active');
        }
      }

      resetProgressIndicators() {
        if (!this.currentProgressLevels) return;
        
        this.currentProgressLevels.forEach(level => {
          if (level) {
            level.classList.remove('active', 'completed');
          }
        });
      }

      goToPreviousReel() {
        // Stop current video
        this.stopAutoplay();
        
        if (!this.currentProgressLevels) return;
        
        if (this.currentReelIndex > 0) {
          // Remove active class from current
          if (this.currentProgressLevels[this.currentReelIndex]) {
            this.currentProgressLevels[this.currentReelIndex].classList.remove('active');
          }
          
          // Go to previous reel in same slide
          this.currentReelIndex--;
          
          // Remove completed from previous and make it active
          if (this.currentProgressLevels[this.currentReelIndex]) {
            this.currentProgressLevels[this.currentReelIndex].classList.remove('completed');
            this.currentProgressLevels[this.currentReelIndex].classList.add('active');
          }
          
          this.startAutoplay();
        } else if (this.currentSlideIndex > 0) {
          // If we're at first reel, go to previous slide
          this.goToPreviousSlide();
        } else {
          // We're at first reel of first slide - restart current reel
          this.restartCurrentReel();
        }
      }

      restartCurrentReel() {
        // Just restart the current reel from beginning
        this.stopAutoplay();
        
        // Reset progress indicator for current reel
        if (this.currentProgressLevels && this.currentProgressLevels[this.currentReelIndex]) {
          this.currentProgressLevels[this.currentReelIndex].classList.remove('active', 'completed');
          this.currentProgressLevels[this.currentReelIndex].classList.add('active');
        }
        
        this.startAutoplay();
      }

      goToNextReel() {
        // Stop current video
        this.stopAutoplay();
        
        if (!this.currentProgressLevels) return;
        
        const currentSlide = this.slider.slides[this.currentSlideIndex];
        const totalReelsInSlide = currentSlide.querySelectorAll('.insta-stories__reel').length;
        
        // Mark current progress as completed
        if (this.currentProgressLevels[this.currentReelIndex]) {
          this.currentProgressLevels[this.currentReelIndex].classList.remove('active');
          this.currentProgressLevels[this.currentReelIndex].classList.add('completed');
        }
        
        if (this.currentReelIndex < totalReelsInSlide - 1) {
          // Move to next reel in same slide
          this.currentReelIndex++;
          
          // Mark new reel as active
          if (this.currentProgressLevels[this.currentReelIndex]) {
            this.currentProgressLevels[this.currentReelIndex].classList.add('active');
          }
          
          this.startAutoplay();
        } else {
          // Move to next slide
          this.goToNextSlide();
        }
      }

      goToPreviousSlide() {
        if (this.currentSlideIndex > 0) {
          this.slider.slidePrev();
        }
      }

      goToNextSlide() {
        if (this.currentSlideIndex < this.totalSlides - 1) {
          this.slider.slideNext();
        } else {
          this.closeModal();
        }
      }

      toggleVolume() {
        // Toggle global mute state
        this.isMuted = !this.isMuted;
        
        // Get current video
        const currentVideo = this.getCurrentVideo();
        if (currentVideo && currentVideo.video) {
          // Apply mute state to current video
          currentVideo.video.muted = this.isMuted;
        }
        
        // Apply mute state to all videos for consistency
        this.videoElements.forEach(videoObj => {
          if (videoObj.video) {
            videoObj.video.muted = this.isMuted;
          }
        });
        
        // Update volume button state
        this.updateVolumeButtonState();
      }

      handleKeydown(e) {
        if (!this.isModalOpen) return;
        
        switch(e.key) {
          case 'Escape':
            this.closeModal();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            this.goToPreviousReel();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.goToNextReel();
            break;
          case ' ':
            e.preventDefault();
            this.togglePlayPause();
            break;
        }
      }

      togglePlayPause() {
        if (this.isPlaying) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }

      resetAllVideos() {
        this.videoElements.forEach(videoObj => {
          if (videoObj.video) {
            videoObj.video.pause();
            videoObj.video.currentTime = 0;
            // Maintain mute state when resetting
            videoObj.video.muted = this.isMuted;
          }
        });
      }

      waitForUserInteraction() {
        // If autoplay is prevented, wait for user interaction
        const handleInteraction = () => {
          this.startAutoplay();
          document.removeEventListener('click', handleInteraction);
          document.removeEventListener('touchstart', handleInteraction);
        };
        
        document.addEventListener('click', handleInteraction);
        document.addEventListener('touchstart', handleInteraction);
      }

      cleanup() {
        this.closeModal();
        
        // Clean up all video event listeners
        this.videoElements.forEach(videoObj => {
          if (videoObj.video && videoObj.handleEnded) {
            videoObj.video.removeEventListener('ended', videoObj.handleEnded);
          }
        });
        
        if (this.slider) {
          this.slider.destroy();
        }
      }
    }
  );
}

if (!customElements.get('size-chart-table')) {
  customElements.define(
    'size-chart-table', 
    class SizeChartTable extends HTMLElement {
      constructor() {
        super();
        this.isCm = true; // Default to CM
        this.originalValues = []; // Store original CM values
        this.toggleHandler = this.toggleMeasurement.bind(this);
      }

      connectedCallback() {
        this.setupToggle();
        this.storeOriginalValues();
      }

      disconnectedCallback() {
        const toggle = this.querySelector('.size-chart__toggle');
        if (toggle) {
          toggle.removeEventListener('click', this.toggleHandler);
        }
      }

      // Store original CM values
      storeOriginalValues() {
        const table = this.querySelector('table');
        if (!table) return;

        const tbody = table.querySelector('tbody');
        const rows = tbody.querySelectorAll('tr');
        
        this.originalValues = Array.from(rows).map(row => {
          const cells = row.querySelectorAll('td');
          return Array.from(cells).slice(1).map(cell => parseFloat(cell.textContent));
        });
      }

      setupToggle() {
        const toggle = this.querySelector('.size-chart__toggle');
        if (toggle) {
          // Set initial data-unit attribute
          toggle.setAttribute('data-unit', 'cm');
          toggle.addEventListener('click', this.toggleHandler);
        }
      }

      toggleMeasurement() {
        this.isCm = !this.isCm;
        
        // Update data-unit attribute on toggle element
        const toggle = this.querySelector('.size-chart__toggle');
        if (toggle) {
          toggle.setAttribute('data-unit', this.isCm ? 'cm' : 'inch');
        }
        
        this.updateTableValues();
      }

      updateTableValues() {
        const table = this.querySelector('table');
        if (!table) return;

        const tbody = table.querySelector('tbody');
        const rows = tbody.querySelectorAll('tr');
        
        rows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll('td');
          // Skip the first cell (size label) and only convert measurement cells
          Array.from(cells).slice(1).forEach((cell, cellIndex) => {
            const originalValue = this.originalValues[rowIndex]?.[cellIndex];
            if (originalValue !== undefined) {
              if (this.isCm) {
                // Convert back to CM (original value)
                cell.textContent = originalValue.toString();
              } else {
                // Convert to inches (CM ÷ 2.54)
                const inchValue = originalValue / 2.54;
                cell.textContent = inchValue.toFixed(1); // Round to 1 decimal place
              }
            }
          });
        });
      }
    }
  );
}

// Initialize category click listeners
document.addEventListener('DOMContentLoaded', () => {
  const storiesElement = document.querySelector('instagram-stories');
  if (storiesElement && !storiesElement.categoryListenersBound) {
    const categories = storiesElement.querySelectorAll('.insta-stories__category');
    
    categories.forEach((category, index) => {
      category.addEventListener('click', () => {
        storiesElement.openModal(index);
      });
    });
    
    // Mark as bound to prevent duplicate listeners
    storiesElement.categoryListenersBound = true;
  }
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', afterDOMLoaded);
} else {
    afterDOMLoaded();
}

function afterDOMLoaded() {
  if (document.body.classList.contains('template-product')) {
   try { stickyATCADA();} catch (err) {console.error('error in custom.js, stickyATCADA function. err:', err);}
  }
}

// Add Aria-Labels to Sticky ATC Icons
function stickyATCADA() {
  const variantDropdownSVGs = document.querySelectorAll('div[data-essential-cart-element="variant-select"] div[data-essential-cart-element="icon"] svg');
  if(variantDropdownSVGs) {
    variantDropdownSVGs.forEach(variantDropdownSVG => {
      variantDropdownSVG.setAttribute("aria-label", "Variant Selection Dropdown")
    })
  }

  const quantityLessButtons = document.querySelectorAll('div[data-essential-cart-element="quantity-select-inner"] button:first-of-type');
  const quantityLessSVGs = document.querySelectorAll('div[data-essential-cart-element="quantity-select-inner"] div[data-essential-cart-element="icon"] svg:has(path[d="M5 10c0-.414.336-.75.75-.75h8.5c.414 0 .75.336.75.75s-.336.75-.75.75h-8.5c-.414 0-.75-.336-.75-.75Z"])');
  if (quantityLessButtons && quantityLessSVGs) {
    quantityLessButtons.forEach(quantityLessButton => {
      quantityLessButton.setAttribute("aria-label", "Reduce item quantity by one");
    })
    quantityLessSVGs.forEach(quantityLessSVG => {
      quantityLessSVG.setAttribute("aria-label", "Reduce item quantity by one");
    })
  }

  const quantityMoreButtons = document.querySelectorAll('div[data-essential-cart-element="quantity-select-inner"] button:last-of-type');
  const quantityMoreSVGs = document.querySelectorAll('div[data-essential-cart-element="quantity-select-inner"] div[data-essential-cart-element="icon"] svg:has(path[d="M10.75 5.75c0-.414-.336-.75-.75-.75s-.75.336-.75.75v3.5h-3.5c-.414 0-.75.336-.75.75s.336.75.75.75h3.5v3.5c0 .414.336.75.75.75s.75-.336.75-.75v-3.5h3.5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-3.5v-3.5Z"])');
  if (quantityMoreButtons && quantityMoreSVGs) {
    quantityMoreButtons.forEach(quantityMoreButton => {
      quantityMoreButton.setAttribute("aria-label", "Increase item quantity by one"); 
    })
    quantityMoreSVGs.forEach(quantityMoreSVG => {
      quantityMoreSVG.setAttribute("aria-label", "Increase item quantity by one");
    })
  }
}

// Mouse enter event
document.addEventListener('mouseover', function(event) {
  const megamenuLink = event.target.closest('[data-megamenu-grandlinks]');
  if (megamenuLink) {
    megamenuHover(megamenuLink);
  }
});

function megamenuHover(link) {
  const megamenu = link.closest('.site-nav__megamenu');
  if (!megamenu) return;

  const megamenuGrandLinks = megamenu.querySelectorAll('.site-nav__megamenu-level3');
  megamenuGrandLinks.forEach((megamenuGrandLink) => { megamenuGrandLink.classList.add('hide') });

  document.getElementById(`${link.dataset.megamenuGrandlinks}`).classList.remove('hide');
}

function toggleRichtext(event) {
  if (event.target.classList.contains('is-active')) {
    event.target.classList.remove('is-active');
    event.target.textContent = 'See More';
    event.target.closest('.collection-richtext').classList.remove('is-open');
  } else {
    event.target.classList.add('is-active');
    event.target.textContent = 'See Less';
    event.target.closest('.collection-richtext').classList.add('is-open');
  }
}

function openMediaModal() {
  const mediaModal = document.getElementById('ProductMediaModal');
  if (!mediaModal) return;

  mediaModal.openModal();
}

class ShopByReel extends HTMLElement {
  constructor() {
    super();

    this.isMediaLazyLoaded = false;
    this.isMuted = true;

    this.items = this.querySelectorAll('.shop-reel__item');
    this.modal = this.querySelector('.shop-reel__modal');
    this.volumeButton = this.querySelector('.shop-reel__volume');

    this.slider = this.querySelector('.shop-reel__modal__slider');
    this.slides = this.querySelectorAll('.shop-reel__modal__slide');
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSlider();
    this.handleClickEvents();
  }

  lazyLoadMedia() {
    if (this.isMediaLazyLoaded) return;
    this.isMediaLazyLoaded = true;

    // Load videos
    const videoElements = this.modal.querySelectorAll('video');
    videoElements.forEach(video => {
      Array.from(video.children).forEach(source => {
        if (source.tagName === 'SOURCE' && source.dataset.src) {
          source.src = source.dataset.src;
        }
      });
      video.load();
      video.muted = false;
    });

    // Load images
    const lazyImages = this.modal.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
  }

  handleClickEvents() {
    this.addEventListener('click', (event) => {
      const item = event.target.closest('.shop-reel__item.has-product');
      if (item) {
        this.openModal(item);
        return;
      }

      const closeButton = event.target.closest('.shop-reel__modal__close');
      if (closeButton || event.target.classList.contains('shop-reel__modal')) {
        this.closeModal();
        return;
      }

      const volumeButton = event.target.closest('.shop-reel__volume');
      if (volumeButton) {
        this.toggleVolume(volumeButton);
        return;
      }

      const addCartButton = event.target.closest('[data-add-cart]');
      if (addCartButton) {
        event.preventDefault();
        this.addItemToCart(addCartButton);
      }
    });
  }

  initSlider() {
    const sliderArgs = {
      wrapAround: this.slides.length > 3,
      adaptiveHeight: false,
      prevNextButtons: true,
      pageDots: false,
    };

    this.flickity = new theme.Slideshow(this.slider, sliderArgs);

    this.flickity.slideshow.on( 'change', (index) => {
      this.playActiveVideo(index);
      this.flickity.goToSlide(index);
    });
  }

  openModal(item) {
    this.lazyLoadMedia();
    this.playActiveVideo(item.dataset.index);
    this.flickity.goToSlide(item.dataset.index);

    document.documentElement.classList.add('overflow-hidden');
    this.modal.classList.add('open');
  }

  closeModal() {
    this.pauseAllVideos();

    document.documentElement.classList.remove('overflow-hidden');
    this.modal.classList.remove('open');
    this.volumeButton.classList.remove('muted');
  }

  playActiveVideo(currentIndex) {
    this.querySelectorAll('.shop-reel__modal__slide').forEach((slide, slideIndex) => {
      const videoElement = slide.querySelector('video');
      if (!videoElement) return;

      if (slideIndex == currentIndex) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
    });
  }

  pauseAllVideos() {
    this.querySelectorAll('.shop-reel__modal__slide').forEach(slide => {
      const videoElement = slide.querySelector('video');
      if (!videoElement) return;

      videoElement.pause();
    });
  }

  toggleVolume(volumeButton) {
    const video = volumeButton.closest('.shop-reel__modal__slide').querySelector('video');
    if (!video) return;

    video.muted = !video.muted;
    volumeButton.classList.toggle('muted', video.muted);
  }

  addItemToCart(addCartButton) {
    const productFormButton = document.querySelector('.product-section .product-single__form [data-add-to-cart]');
    if (!productFormButton) return;

    productFormButton.click();
  }
}

if (!customElements.get('shop-by-reel')) {
  customElements.define('shop-by-reel', ShopByReel);
}

class ProductMediaModal extends HTMLElement {
  constructor() {
    super();

    this.isMediaLazyLoaded = false;
    
    // Cache DOM elements
    this.tabButtons = this.querySelectorAll('[data-tab]');
    this.tabPanels = this.querySelectorAll('[data-tab-content]');
    this.mediaImages = this.querySelectorAll('[data-image]');
    this.imageThumbnails = this.querySelectorAll('[data-image-thumb]');
    this.mediaVideos = this.querySelectorAll('[data-video]');
    this.videoThumbnails = this.querySelectorAll('[data-video-thumb]');
    
    // Bind methods
    this.handleModalInteraction = this.handleModalInteraction.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  connectedCallback() {
    this.initializeEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleModalInteraction);
  }

  initializeEventListeners() {
    this.addEventListener('click', this.handleModalInteraction);
  }

  handleModalInteraction(event) {
    const target = event.target;
    const tabButton = target.closest('[data-tab]');
    
    if (tabButton) {
      this.switchActiveTab(tabButton);
      return;
    }

    const closeButton = target.closest('.product-media-modal__close');
    if (closeButton || target.classList.contains('product-media-modal')) {
      this.closeModal();
      return;
    }

    const imageThumbnail = target.closest('[data-image-thumb]');
    if (imageThumbnail) {
      this.showImage(imageThumbnail.dataset.index);
      return;
    }

    const videoThumbnail = target.closest('[data-video-thumb]');
    if (videoThumbnail) {
      this.showVideo(videoThumbnail.dataset.index);
    }
  }

  lazyLoadMedia() {
    if (this.isMediaLazyLoaded) return;
    this.isMediaLazyLoaded = true;

    // Load videos
    const videoElements = this.querySelectorAll('video');
    videoElements.forEach(video => {
      Array.from(video.children).forEach(source => {
        if (source.tagName === 'SOURCE' && source.dataset.src) {
          source.src = source.dataset.src;
        }
      });
      video.load();
      video.muted = false;
    });

    // Load images
    const lazyImages = this.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
  }

  switchActiveTab(selectedTab) {
    // Update active tab
    this.tabButtons.forEach(tab => tab.classList.remove('is-active'));
    selectedTab.classList.add('is-active');

    // Show corresponding content
    this.tabPanels.forEach(panel => panel.classList.add('hide'));
    this.querySelector(`#MediaModal-${selectedTab.dataset.id}`).classList.remove('hide');

    // Handle media playback
    if (selectedTab.dataset.tab === 'videos') {
      this.playActiveVideo();
    } else {
      this.pauseAllVideos();
    }
  }

  showImage(imageIndex) {
    this.mediaImages.forEach(img => {
      img.classList.toggle('hide', imageIndex != img.dataset.index);
    });
    
    this.imageThumbnails.forEach(thumb => {
      thumb.classList.toggle('active', imageIndex == thumb.dataset.index);
    });
  }

  showVideo(videoIndex) {
    this.mediaVideos.forEach(video => {
      const videoElement = video.querySelector('video');
      if (videoIndex == video.dataset.index) {
        video.classList.remove('hide');
        videoElement.play();
      } else {
        video.classList.add('hide');
        videoElement.pause();
      }
    });
  }

  playActiveVideo() {
    this.mediaVideos.forEach(video => {
      const videoElement = video.querySelector('video');
      if (!video.classList.contains('hide')) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
    });
  }

  pauseAllVideos() {
    this.mediaVideos.forEach(video => {
      video.querySelector('video').pause();
    });
  }

  getCurrentMediaIndex() {
    const slider = document.querySelector('[data-product-photos]');
    const flickity = slider ? Flickity.data(slider) : null;
    return flickity ? flickity.selectedIndex : 0;
  }

  displayCurrentMedia() {
    const currentIndex = this.getCurrentMediaIndex();
    const isVideo = Array.from(this.mediaVideos).some(
      video => video.dataset.index == currentIndex
    );

    if (isVideo) {
      this.querySelector('[data-tab="videos"]')?.click();
      this.showVideo(currentIndex);
    } else {
      this.querySelector('[data-tab="images"]')?.click();
      this.showImage(currentIndex);
    }
  }

  openModal() {
    this.lazyLoadMedia();
    this.displayCurrentMedia();

    requestAnimationFrame(() => {
      document.documentElement.classList.add('overflow-hidden');
      this.classList.add('open');
    });
  }

  closeModal() {
    this.pauseAllVideos();
    document.documentElement.classList.remove('overflow-hidden');
    this.classList.remove('open');
  }
}

if (!customElements.get('product-media-modal')) {
  customElements.define('product-media-modal', ProductMediaModal);
}

class CartRemoveButton extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    this.btn = this.querySelector('button');

    if (this.btn) {
      this.btn.addEventListener('click', this.onRemove.bind(this));
    }
  }

  onRemove() {
    this.btn.classList.add('is-loading');
    this.closest('.cart__item').querySelector('.js-qty__wrapper').classList.add('is-loading');

    theme.cart.changeItem(this.key, 0)
      .then(cart => {
        const parsedCart = JSON.parse(cart);

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: parsedCart }
        }));

        // Triggers CartForm.buildCart() which rebuilds the drawer HTML
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(err => {
        console.error('Remove item error:', err);
        this.btn.classList.remove('is-loading');
        this.closest('.cart__item').querySelector('.js-qty__wrapper').classList.remove('is-loading');
      });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// ─── Shared Validation Registry ─────────────────────────────────────────────

function registerFormValidator(form, validatorFn) {
  if (!form._validators) form._validators = [];
  form._validators.push(validatorFn);
}

function runFormValidators(form) {
  if (!form._validators || form._validators.length === 0) return true;
  return form._validators.map(fn => fn()).every(Boolean);
}

// Validate visible custom radio option groups (line-item options)
function validateLineItemRadioOptions(form) {
  if (!form) return true;
  let firstInvalid = null;

  document.querySelectorAll('.line-item__option').forEach((optionEl) => {
    if (optionEl.classList.contains('hide')) return;
    if (optionEl.tagName === 'BUNDLE-LINE-OPTIONS') return;

    const radios = [...optionEl.querySelectorAll('input[type="radio"]')].filter((radio) => {
      const wrap = radio.closest('.variant-input');
      return wrap && !wrap.classList.contains('hide') && !radio.disabled;
    });
    if (!radios.length) return;

    const hasSelection = radios.some((radio) => radio.checked);
    optionEl.classList.toggle('line-item__option--error', !hasSelection);
    if (!hasSelection && !firstInvalid) firstInvalid = optionEl;
  });

  if (firstInvalid) {
    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  }
  return true;
}

function ensureLineItemRadioValidators(form) {
  if (!form || form._lineItemRadioValidatorBound) return;
  form._lineItemRadioValidatorBound = true;
  registerFormValidator(form, () => validateLineItemRadioOptions(form));
}

// Expose for GoKwik Buy Now
window.registerFormValidator = registerFormValidator;
window.runFormValidators = runFormValidators;
window.ensureLineItemRadioValidators = ensureLineItemRadioValidators;

// ─── QuantityPickerV2 ────────────────────────────────────────────────────────

class QuantityPickerV2 extends HTMLElement {
  connectedCallback() {
    this._suffix  = this.dataset.suffix || 'Box';
    this._hidden  = this.querySelector('[data-input-value]');
    this._display = this.querySelector('.qty-picker-v2__value');
    this._plus    = this.querySelector('.qty-plus');
    this._minus   = this.querySelector('.qty-minus');

    this.min  = parseInt(this._hidden.min) ?? 0;
    this.max  = parseInt(this._hidden.max) || Infinity;
    this._qty = parseInt(this._hidden.value) || 0;

    this._plus.addEventListener('click',  () => this._step(1));
    this._minus.addEventListener('click', () => this._step(-1));

    this._render();
  }

  _step(delta) {
    const next = this._qty + delta;
    if (next < this.min || next > this._effectiveMax()) return;
    this._qty = next;
    this._render();
    this.dispatchEvent(new CustomEvent('qty:change', {
      bubbles: true,
      detail: { quantity: this._qty }
    }));
  }

  _effectiveMax() {
    return Math.min(this.max, this._externalMax ?? Infinity);
  }

  setExternalMax(n) {
    this._externalMax = Math.max(this.min, n);
    this._render();
  }

  _render() {
    this._hidden.value = this._qty;
    this._hidden.dataset.inputValue = this._qty;
    this._display.textContent = `${this._qty} ${this._suffix}`;

    if (this.hasAttribute('data-disable-on-zero')) {
      if (this._qty === 0) {
        this._hidden.setAttribute('disabled', '');
      } else {
        this._hidden.removeAttribute('disabled');
      }
    }

    const atMin = this._qty <= this.min;
    const atMax = this._qty >= this._effectiveMax();

    this._minus.setAttribute('aria-disabled', atMin);
    this._plus.setAttribute('aria-disabled',  atMax);
  }

  get value() { return this._qty; }

  set value(n) {
    this._qty = Math.min(this._effectiveMax(), Math.max(this.min, parseInt(n) || 0));
    this._render();
  }
}

customElements.define('quanity-picker-2', QuantityPickerV2);

// ─── BundleLineOptions ───────────────────────────────────────────────────────

class BundleLineOptions extends HTMLElement {
  connectedCallback() {
    this._cap     = this._parseCap();
    this._pickers = Array.from(this.querySelectorAll('quanity-picker-2'));
    this._error   = this.querySelector('.line-item__option-error');

    this.addEventListener('qty:change', () => this._onPickerChange());

    const formId = this.dataset.form;
    if (formId) {
      this._form = document.getElementById(formId);
      if (this._form) {
        registerFormValidator(this._form, () => this._validate());
      }
    }

    this._onVariantChange = (e) => this._handleVariantChange(e);
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _handleVariantChange(e) {
    const selectedValues = e.detail.currentlySelectedValues.map(item => item.value);
    for (const val of selectedValues) {
      const num = parseInt(val.trim());
      if (!isNaN(num)) {
        this._cap = num;
        this._resetPickers();
        return;
      }
    }
  }

  _resetPickers() {
    for (const picker of this._pickers) {
      picker.value = 0;
      picker.setExternalMax(Infinity);
    }
    this._onPickerChange();
    this._clearError();
  }

  _parseCap() {
    const values = (this.dataset.selectedValues || '').split(',');
    for (const val of values) {
      const num = parseInt(val.trim());
      if (!isNaN(num)) return num;
    }
    return Infinity;
  }

  _getTotal() {
    return this._pickers.reduce((sum, p) => sum + p.value, 0);
  }

  _validate() {
    const total = this._getTotal();
    if (total !== this._cap) {
      this._showError(`Total must equal ${this._cap} slots (${total}/${this._cap} selected)`);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    this._clearError();
    return true;
  }

  _onPickerChange() {
    const remaining = this._cap - this._getTotal();
    for (const picker of this._pickers) {
      picker.setExternalMax(picker.value + remaining);
    }
    this._clearError();
  }

  _showError(msg) {
    if (!this._error) return;
    this._error.textContent = msg;
    this._error.setAttribute('aria-live', 'polite');
    this._error.classList.remove('hide');
  }

  _clearError() {
    if (!this._error) return;
    this._error.textContent = '';
    this._error.classList.add('hide');
  }
}

customElements.define('bundle-line-options', BundleLineOptions);

// ─── ProductPersonalization ──────────────────────────────────────────────────

class ProductPersonalization extends HTMLElement {
  constructor() {
    super();
    this._bgRenderTimer = null;
    this._maxLines = 2;
  }

  connectedCallback() {
    this._bindTabs();
    this._applyInitialTabState();
    this._bindAddLine();
    this._bindExistingLines();
    this._bindLogoUpload();
    this._bindFormSubmit();

    document.fonts.ready.then(() => this._updatePreview());
  }

  _bindTabs() {
    const tabs = this.querySelectorAll('.personalization__tab');
    // If there's only one tab (or none), there's nothing to bind — the single
    // visible panel is already active by default via _applyInitialTabState.
    if (tabs.length < 2) return;

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        const targetId = tab.dataset.tab;
        this.querySelectorAll('.personalization__content').forEach(panel => {
          const isTarget = panel.dataset.tabId === targetId;
          panel.classList.toggle('hide', !isTarget);
          panel.querySelectorAll('input, select, textarea').forEach(el => {
            if (!el.dataset.generated) el.disabled = !isTarget;
          });
        });

        this._updateGeneratedFileInputs();
        if (targetId === 'text') this._updatePreview();
      });
    });
  }

  _applyInitialTabState() {
    // When only one content panel exists (single-option mode) it has no
    // corresponding tab with an 'active' class, so we enable it unconditionally.
    const panels = this.querySelectorAll('.personalization__content');
    const hasTabs = this.querySelectorAll('.personalization__tab').length >= 2;

    panels.forEach(panel => {
      // A panel is active when: there are real tabs and this one isn't hidden,
      // OR there are no tabs (single-option mode) — always active.
      const isActive = hasTabs ? !panel.classList.contains('hide') : true;
      panel.querySelectorAll('input, select, textarea').forEach(el => {
        if (!el.dataset.generated) el.disabled = !isActive;
      });
    });
  }

  _bindAddLine() {
    const btn = this.querySelector('[data-add-line]');
    if (!btn) return;
    btn.addEventListener('click', () => this._addLine());
  }

  _addLine() {
    const container = this.querySelector('.personalization__text-lines');
    if (!container) return;

    const existing = container.querySelectorAll('.personalization__text-line').length;
    if (existing >= this._maxLines) return;

    const lineNum = existing + 1;
    const div = document.createElement('div');
    div.className = 'personalization__text-line';
    div.innerHTML = this._lineHTML(lineNum);
    container.appendChild(div);

    this._bindLineInputs(div);
    this._reindex();
    this._updatePreview();
  }

  _removeLine(lineEl) {
    lineEl.remove();
    this._reindex();
    this._updatePreview();
  }

  _reindex() {
    const container = this.querySelector('.personalization__text-lines');
    if (!container) return;

    const lines = container.querySelectorAll('.personalization__text-line');
    const addBtn = this.querySelector('[data-add-line]');

    lines.forEach((line, i) => {
      const n = i + 1;
      const label = line.querySelector('.personalization__line-label');
      if (label) label.textContent = `Line ${n}`;

      const textInput = line.querySelector('input[type="text"]');
      if (textInput) {
        textInput.name = `properties[Line ${n}]`;
        textInput.placeholder = `Enter text for line ${n}`;
      }
      const fontSelect = line.querySelector('select');
      if (fontSelect) fontSelect.name = `properties[Line ${n} Font]`;

      const sizeInput = line.querySelector('input[type="range"]');
      if (sizeInput) sizeInput.name = `properties[Line ${n} Size]`;

      let removeBtn = line.querySelector('.personalization__remove-line');
      if (n === 1) {
        if (removeBtn) removeBtn.remove();
      } else {
        if (!removeBtn) {
          removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'personalization__remove-line';
          removeBtn.textContent = '✕ Remove';
          removeBtn.addEventListener('click', () => this._removeLine(line));
          line.appendChild(removeBtn);
        }
      }
    });

    // Guard: addBtn may not exist when text option is disabled.
    if (addBtn) {
      addBtn.style.display = lines.length >= this._maxLines ? 'none' : '';
    }
  }

  _bindExistingLines() {
    this.querySelectorAll('.personalization__text-line').forEach(line => {
      this._bindLineInputs(line);
    });
    this._reindex();
  }

  _bindLineInputs(lineEl) {
    lineEl.querySelectorAll('input, select').forEach(el => {
      el.addEventListener('input', () => this._updatePreview());
      el.addEventListener('change', () => this._updatePreview());
    });
  }

  _bindLogoUpload() {
    const fileInput = this.querySelector('#PersonalizationLogo');
    if (!fileInput) return;

    let _lastValidFile = null;

    fileInput.addEventListener('change', () => {
      const label = this.querySelector(`label[for="${fileInput.id}"]`);
      if (fileInput.files.length > 0) {
        _lastValidFile = fileInput.files[0];
        if (label) {
          label.textContent = _lastValidFile.name;
          label.classList.remove('field-error');
        }
      } else if (_lastValidFile) {
        const dt = new DataTransfer();
        dt.items.add(_lastValidFile);
        fileInput.files = dt.files;
      }
    });
  }

  _updatePreview() {
    const previewContainer = this.querySelector('.personalization__text-preview');
    // Text tab may not be rendered when only the logo option is enabled.
    if (!previewContainer) return;

    let canvas = previewContainer.querySelector('canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.width = '100%';
      canvas.style.borderRadius = '6px';
      canvas.style.display = 'block';
      previewContainer.appendChild(canvas);
    }

    const ctx = canvas.getContext('2d');
    const linesData = [];
    let totalHeight = 60;

    this.querySelectorAll('.personalization__text-line').forEach(line => {
      const text = line.querySelector('input[type="text"]')?.value?.trim() || '';
      const font = (line.querySelector('select')?.value || 'sans-serif').replace(/"/g, "'");
      const size = parseInt(line.querySelector('input[type="range"]')?.value || 34) * 2;

      if (text) {
        totalHeight += size * 1.3;
        linesData.push({ text, font, size });
      }
    });

    if (linesData.length === 0) {
      canvas.style.display = 'none';
      let empty = previewContainer.querySelector('.personalization__preview-empty');
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'personalization__preview-empty';
        empty.textContent = 'Your personalised text will preview here.';
        previewContainer.appendChild(empty);
      }
      empty.style.display = '';
      return;
    }

    const empty = previewContainer.querySelector('.personalization__preview-empty');
    if (empty) empty.style.display = 'none';
    canvas.style.display = 'block';

    canvas.width = 800;
    canvas.height = Math.max(totalHeight + 20, 200);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let currentY = 50;
    let svgParts = '';

    linesData.forEach(line => {
      ctx.font = `${line.size}px ${line.font}`;
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(line.text, canvas.width / 2, currentY);

      const escapedText = line.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      svgParts += `<text x="50%" y="${currentY + line.size * 0.8}" font-family="${line.font}" font-size="${line.size}px" fill="#000000" text-anchor="middle">${escapedText}</text>`;
      currentY += line.size * 1.3;
    });

    clearTimeout(this._bgRenderTimer);
    this._bgRenderTimer = setTimeout(() => {
      this._generateFiles(canvas, svgParts, currentY + 40);
    }, 350);
  }

  _clearPreview() {
    const canvas = this.querySelector('canvas');
    if (canvas) canvas.style.display = 'none';
  }

  _generateFiles(canvas, svgContent, totalHeight) {
    canvas.toBlob(blob => {
      if (!blob) return;
      this._setFileInput('generated-jpeg-file', blob, 'customer_design.jpg', 'image/jpeg');
    }, 'image/jpeg', 0.95);

    const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="${totalHeight}" viewBox="0 0 800 ${totalHeight}">${svgContent}</svg>`;
    const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml' });
    this._setFileInput('generated-svg-file', svgBlob, 'production_vector.svg', 'image/svg+xml');
  }

  _setFileInput(inputId, blob, filename, mimeType) {
    const input = document.getElementById(inputId);
    // Input may not exist when the text tab is disabled/not rendered.
    if (!input) return;
    try {
      const file = new File([blob], filename, { type: mimeType });
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn(`[ProductPersonalization] Could not set ${inputId}:`, e);
    }
  }

  _updateGeneratedFileInputs() {
    const activeTab = this.querySelector('.personalization__tab.active')?.dataset.tab;
    const shouldEnable = activeTab === 'text';

    ['generated-jpeg-file', 'generated-svg-file'].forEach(id => {
      const input = document.getElementById(id);
      // Guard: these inputs are only rendered when the text option is enabled.
      if (input) input.disabled = !shouldEnable;
    });
  }

  _bindFormSubmit() {
    const formId = this.dataset.form;
    const form = (formId && document.getElementById(formId))
      || document.querySelector('[name="add"]')?.closest('form')
      || this.closest('form');
    if (!form) return;

    // Shared with Buy Now / GoKwik via runFormValidators(form)
    registerFormValidator(form, () => this._validate());

    const atcButton = form.querySelector('[name="add"]');
    if (!atcButton) return;

    atcButton.addEventListener('click', e => {
      if (!runFormValidators(form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      const activeTab = this.querySelector('.personalization__tab.active')?.dataset.tab;
      const hasFile = activeTab === 'text' &&
        document.getElementById('generated-jpeg-file')?.files?.length > 0;

      if (hasFile) {
        e.stopImmediatePropagation();
        form.enctype = 'multipart/form-data';
        form.method  = 'POST';
        form.action  = '/cart/add';
        HTMLFormElement.prototype.submit.call(form);
      }
    }, true);
  }

  _validate() {
    const activeTab = this.querySelector('.personalization__tab.active')?.dataset.tab;

    // When there are no tabs (single-option mode) derive the active tab from
    // whichever content panel is present and not hidden.
    const resolvedTab = activeTab || this._resolveActiveTabFromPanels();

    if (resolvedTab === 'text') {
      const inputs = [...this.querySelectorAll('.personalization__text-line input[type="text"]')];
      inputs.forEach(i => i.classList.remove('field-error'));

      const emptyInputs = inputs.filter(i => i.value.trim() === '');
      emptyInputs.forEach(i => i.classList.add('field-error'));

      if (emptyInputs.length > 0) {
        emptyInputs[0].focus();
        return false;
      }
    } else if (resolvedTab === 'logo') {
      const fileInput = this.querySelector('#PersonalizationLogo');
      const label = fileInput ? this.querySelector(`label[for="${fileInput.id}"]`) : null;
      if (label) label.classList.remove('field-error');
      if (!fileInput || fileInput.files.length === 0) {
        if (label) label.classList.add('field-error');
        return false;
      }
    }

    return true;
  }

  // Returns the tab ID of the first visible (non-hidden) content panel.
  // Used when the tab bar is not rendered (single-option mode).
  _resolveActiveTabFromPanels() {
    const panel = [...this.querySelectorAll('.personalization__content')]
      .find(p => !p.classList.contains('hide'));
    return panel?.dataset.tabId || null;
  }

  _lineHTML(n) {
    const formId = this.dataset.form || '';
    const formAttr = formId ? `form="${formId}"` : '';
    return `
      <div class="grid grid--small">
        <div class="grid__item">
          <label class="personalization__line-label">Line ${n}</label>
          <input type="text" name="properties[Line ${n}]" placeholder="Enter text for line ${n}" ${formAttr}>
        </div>
        <div class="grid__item small--one-half medium-up--one-half">
          <label>Font Style</label>
          <select name="properties[Line ${n} Font]" ${formAttr}>
            <option value="'BBH Bartle', sans-serif" style="font-family: 'BBH Bartle', sans-serif;">BBH Bartle</option>
            <option value="'Boldonse', sans-serif" style="font-family: 'Boldonse', sans-serif;">Boldonse</option>
            <option value="'Bungee', sans-serif" style="font-family: 'Bungee', sans-serif;">Bungee</option>
            <option value="'Chango', sans-serif" style="font-family: 'Chango', sans-serif;">Chango</option>
            <option value="'League Spartan', sans-serif" style="font-family: 'League Spartan', sans-serif;">League Spartan</option>
            <option value="'Oleo Script Swash Caps', cursive" style="font-family: 'Oleo Script Swash Caps', cursive;">Oleo Script</option>
            <option value="'Pacifico', cursive" style="font-family: 'Pacifico', cursive;">Pacifico</option>
          </select>
        </div>
        <div class="grid__item small--one-half medium-up--one-half">
          <label>Font Size</label>
          <input type="range" name="properties[Line ${n} Size]" min="16" max="90" value="34" ${formAttr}>
        </div>
      </div>
    `;
  }
}

customElements.define('product-personalization', ProductPersonalization);

const footerCollapsibles = document.querySelectorAll('.footer__collapsible-trigger');
footerCollapsibles.forEach((collapsible) => {
  collapsible.addEventListener('click', () => {
    collapsible.parentElement.classList.toggle('is-open');
  });
});

function applyConditions(fieldset, selectedValues) {
  let hasVisibleInput = false;
  let firstVisibleInput = null;
  let currentlyCheckedVisible = null;

  fieldset.querySelectorAll('.variant-input').forEach((inputWrapper) => {
    const input = inputWrapper.querySelector('input');
    const conditionedInput = inputWrapper.querySelector('input[data-conditions]');

    if (!input) return;

    // No data-conditions = always visible
    if (!conditionedInput) {
      inputWrapper.classList.remove('hide');
      hasVisibleInput = true;
      if (!firstVisibleInput) firstVisibleInput = input;
      if (input.checked) currentlyCheckedVisible = input;
      return;
    }

    // Has data-conditions = check against selectedValues
    const conditions = conditionedInput.getAttribute('data-conditions')
      .split('/')
      .map(c => c.trim());

    const isMatch = conditions.some(condition => selectedValues.includes(condition));

    if (isMatch) {
      inputWrapper.classList.remove('hide');
      hasVisibleInput = true;
      if (!firstVisibleInput) firstVisibleInput = input;
      if (input.checked) currentlyCheckedVisible = input;
    } else {
      inputWrapper.classList.add('hide');
      input.checked = false;
    }
  });

  if (firstVisibleInput) {
    if (!currentlyCheckedVisible) {
      firstVisibleInput.checked = true;
      firstVisibleInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const activeInput = currentlyCheckedVisible || firstVisibleInput;
    const lineItem = fieldset.closest('.line-item__option');

    if (lineItem) {
      const selectedValueEl = lineItem.querySelector('[data-option-selected-value]');
      if (selectedValueEl) {
        selectedValueEl.textContent = activeInput.value;
      }
    }
  }

  const lineItem = fieldset.closest('.line-item__option');
  if (lineItem) {
    lineItem.classList.toggle('hide', !hasVisibleInput);
  }
}

// On page load: read initial selected values from data-selected-values attribute
document.querySelectorAll('.line-item__option').forEach((fieldset) => {
  const raw = fieldset.getAttribute('data-selected-values') || '';
  const selectedValues = raw.split('/').map(s => s.trim()).filter(Boolean);
  applyConditions(fieldset, selectedValues);
});

// On input change: update the label display
document.querySelectorAll('.line-item__option').forEach((fieldset) => {
  fieldset.querySelectorAll('input').forEach((input) => {
    input.addEventListener('change', () => {
      if (!input.checked) return;
      const lineItem = fieldset.closest('.line-item__option');
      if (lineItem) {
        lineItem.classList.remove('line-item__option--error');
        const selectedValueEl = lineItem.querySelector('[data-option-selected-value]');
        if (selectedValueEl) {
          selectedValueEl.textContent = input.value;
        }
      }
    });
  });
});

// On variant change: re-apply conditions with new selected values
document.addEventListener('variant:change', (e) => {
  const selectedValues = e.detail.currentlySelectedValues.map(item => item.value);
  document.querySelectorAll('.line-item__option').forEach((fieldset) => {
    applyConditions(fieldset, selectedValues);
  });
});

document.querySelectorAll('[data-scroll-to-target]').forEach((trigger) => {
  trigger.addEventListener('click', (event) => {
    event.preventDefault();

    if (!trigger.getAttribute('data-scroll-to-target')) return;
    
    const target = document.querySelector(trigger.getAttribute('data-scroll-to-target'));
    if (!target) window.location.href = trigger.href;
    
    const header = document.getElementById('SiteHeader');
    const offset = header ? header.offsetHeight : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset - 20;

    window.scrollTo({ top, behavior: 'smooth' });
  });
});

// Bind line-item radio validators to product forms
document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
  ensureLineItemRadioValidators(form);
});